import React from 'react';

/* ============================================================================
 * DonutStatus — SVG donut chart of farm status distribution (ok/warn/off).
 * Ported from the Claude Design prototype (admin-dashboard.jsx) to TSX.
 * ========================================================================= */

export interface DonutStatusProps {
  ok: number;
  warn: number;
  off: number;
  size?: number;
}

interface Segment {
  v: number;
  color: string;
}

export function DonutStatus({ ok, warn, off, size = 128 }: DonutStatusProps): React.ReactElement {
  const total = ok + warn + off || 1;
  const r = size / 2 - 14;
  const c = 2 * Math.PI * r;
  const segs: Segment[] = [
    { v: ok, color: 'var(--gs-primary-50)' },
    { v: warn, color: 'var(--gs-warning)' },
    { v: off, color: '#B8BEB2' },
  ];
  let acc = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--md-sys-color-surface-container-high)"
          strokeWidth={14}
        />
        {segs.map((s, i) => {
          const len = (s.v / total) * c;
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={14}
              strokeLinecap="round"
              strokeDasharray={`${Math.max(0, len - 3)} ${c}`}
              strokeDashoffset={-acc}
            />
          );
          acc += len;
          return el;
        })}
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          className="gs-num"
          style={{
            font: '700 30px/1 var(--gs-font-kr)',
            letterSpacing: '-1px',
            color: 'var(--md-sys-color-on-surface)',
          }}
        >
          {total}
        </span>
        <span
          style={{
            font: '500 11px/1 var(--gs-font-kr)',
            color: 'var(--md-sys-color-on-surface-variant)',
            marginTop: 5,
          }}
        >
          전체 농장
        </span>
      </div>
    </div>
  );
}

export interface LegendRowProps {
  color: string;
  label: string;
  value: number;
  total: number;
}

export function LegendRow({ color, label, value, total }: LegendRowProps): React.ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span
        style={{ width: 9, height: 9, borderRadius: 9999, background: color, flexShrink: 0 }}
      />
      <span
        style={{
          flex: 1,
          font: '500 13px/18px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface)',
        }}
      >
        {label}
      </span>
      <span
        className="gs-num"
        style={{
          font: '700 14px/18px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface)',
        }}
      >
        {value}
      </span>
      <span
        className="gs-num"
        style={{
          font: '400 12px/18px var(--gs-font-kr)',
          color: 'var(--md-sys-color-on-surface-variant)',
          width: 38,
          textAlign: 'right',
        }}
      >
        {Math.round((value / (total || 1)) * 100)}%
      </span>
    </div>
  );
}
